import clsx from "clsx";

import type { NeededBy, NeededByLink } from "../api/types";
import { SheetIcon } from "./SheetIcon";

interface NeededByLineProps {
  neededBy: NeededBy | null | undefined;
  /** The deliverables the field is picked for, by name (`needed_by_links`). When there are some, they are shown as chips. */
  links?: readonly NeededByLink[] | null;
  className?: string;
}

/**
 * The "needed by" line of a card: the deliverables that read the element, as one chip each, or, for older work, the free
 * text it was given. It stands once on the element's card and the same kind letter is used as on the sheet icons.
 */
export function NeededByLine({ neededBy, links, className }: NeededByLineProps) {
  const picked = links ?? [];
  if (!neededBy && picked.length === 0) return null;
  if (picked.length > 0) {
    return (
      <p className={clsx("needed-by", className)}>
        <span className="needed-by__label">Needed by</span>{" "}
        {picked.map((link) => (
          <span key={link.document_kind_id} className="chip needed-by__chip">
            {link.letter && <SheetIcon kind={link.letter} size={14} />} {link.name}
          </span>
        ))}
      </p>
    );
  }
  if (!neededBy) return null;
  // Text without a pick was written before the pick existed: it is kept, and the note says so (NB-01).
  const oldText = !neededBy.not_named_yet && !!neededBy.text && (neededBy.deliverable_ids ?? []).length === 0;
  return (
    <p className={clsx("needed-by", className)}>
      <span className="needed-by__label">Needed by</span>{" "}
      {neededBy.deliverable_kind && <SheetIcon kind={neededBy.deliverable_kind} size={16} />}{" "}
      <span className="needed-by__text">{neededBy.not_named_yet ? "No deliverable named yet" : neededBy.text}</span>
      {neededBy.not_named_yet && (
        <span className="needed-by__hint muted"> Name a deliverable that reads it and it is added.</span>
      )}
      {oldText && <span className="needed-by__hint muted"> Written before deliverables could be picked. Pick the deliverables that read it.</span>}
    </p>
  );
}
